import * as React from "react";
import { Chart, useChart } from "@chakra-ui/charts";
import { Box, Card, HStack, Tabs, Text } from "@chakra-ui/react";
import { Bar, BarChart, LabelList, XAxis, YAxis } from "recharts";

const MODES = {
  asistencias: {
    label: "Asistencias",
    color: "green.solid",
    empty: "Sin asistencias registradas",
  },
  preceptorias: {
    label: "Preceptorias",
    color: "orange.solid",
    empty: "Sin preceptorias registradas",
  },
};

function InstructorStatsChart({ data = [], mode = "asistencias", onModeChange, ...props }) {
  const currentMode = MODES[mode] ? mode : "asistencias";

  const chartData = React.useMemo(() => {
    return [...data]
      .map((item) => ({
        id: item.id,
        name: item.nombre,
        shortName: item.nombre.length > 16 ? `${item.nombre.slice(0, 15)}...` : item.nombre,
        asistencias: Number(item.asistenciasCount ?? 0),
        preceptorias: Number(item.preceptoriasCount ?? 0),
      }))
      .sort((a, b) => b[currentMode] - a[currentMode] || a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [data, currentMode]);

  const totals = React.useMemo(
    () =>
      data.reduce(
        (acc, item) => ({
          asistencias: acc.asistencias + Number(item.asistenciasCount ?? 0),
          preceptorias: acc.preceptorias + Number(item.preceptoriasCount ?? 0),
        }),
        { asistencias: 0, preceptorias: 0 },
      ),
    [data],
  );

  const chart = useChart({
    data: chartData,
    series: [
      { name: "asistencias", color: MODES.asistencias.color },
      { name: "preceptorias", color: MODES.preceptorias.color },
    ],
  });

  const series = chart.getSeries({ name: currentMode });
  const activeTotal = totals[currentMode] ?? 0;
  const empty = chartData.length === 0 || activeTotal === 0;

  return (
    <Card.Root
      bg="dashboard.bg.surface"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      rounded="card"
      {...props}
    >
      <Tabs.Root
        value={currentMode}
        onValueChange={(e) => {
          if (e.value) onModeChange?.(e.value);
        }}
      >
        <Card.Header gap="4" alignItems="stretch">
          <HStack justify="space-between" align="flex-start" gap="4" wrap="wrap">
            <Box>
              <Text fontFamily="display" fontWeight="700" fontSize="lg" color="dashboard.fg.default">
                Actividad por instructor
              </Text>
              <Text fontSize="sm" color="dashboard.fg.muted">
                {MODES[currentMode].label} registradas en el periodo actual
              </Text>
            </Box>

            <Tabs.List bg="dashboard.bg.surfaceMuted" p="1" rounded="lg">
              <Tabs.Trigger value="asistencias" rounded="md" px="3">
                Asistencias
              </Tabs.Trigger>
              <Tabs.Trigger value="preceptorias" rounded="md" px="3">
                Preceptorias
              </Tabs.Trigger>
              <Tabs.Indicator rounded="md" />
            </Tabs.List>
          </HStack>
        </Card.Header>

        <Card.Body pt="8">
          <Tabs.Content value="asistencias" p="0">
            <ChartContent
              empty={empty}
              emptyText={MODES[currentMode].empty}
              chart={chart}
              series={series}
              currentMode={currentMode}
            />
          </Tabs.Content>
          <Tabs.Content value="preceptorias" p="0">
            <ChartContent
              empty={empty}
              emptyText={MODES[currentMode].empty}
              chart={chart}
              series={series}
              currentMode={currentMode}
            />
          </Tabs.Content>
        </Card.Body>
      </Tabs.Root>
    </Card.Root>
  );
}

function ChartContent({ empty, emptyText, chart, series, currentMode }) {
  if (empty) {
    return (
      <Box
        minH="260px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderWidth="1px"
        borderColor="dashboard.border.muted"
        rounded="card"
        bg="dashboard.bg.surfaceMuted"
      >
        <Text color="dashboard.fg.muted" fontSize="sm">
          {emptyText}
        </Text>
      </Box>
    );
  }

  return (
    <Chart.Root height="260px" chart={chart}>
      <BarChart data={chart.data} layout="vertical" margin={{ top: 8, right: 28, bottom: 8, left: 8 }}>
        <XAxis type="number" allowDecimals={false} axisLine={false} tickLine={false} />
        <YAxis
          type="category"
          dataKey={chart.key("shortName")}
          width={112}
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 12 }}
        />
        <Bar dataKey={chart.key(currentMode)} fill={chart.color(series?.color)} radius={[0, 6, 6, 0]}>
          <LabelList dataKey={chart.key(currentMode)} position="right" fontSize={12} />
        </Bar>
      </BarChart>
    </Chart.Root>
  );
}

export default InstructorStatsChart;
